/**
 * convAiSessionApi.ts
 *
 * Thin API layer for the BFF Conv-AI session-key endpoint (interim bridge
 * until TT-Auth). Mints a short-lived opaque key the browser sends to the
 * Conv-AI service as `Authorization: Bearer <key>`.
 *
 * Endpoint: POST /drt/api/bff/conv-ai-session   (requires the BFF session cookie)
 * Response: { token, tokenType: 'Bearer', expiresAt (ISO-8601), expiresInSeconds }
 *
 * Token lifetime is configured on the BFF (default 10 min, hard cap 15 min,
 * clamped to the primary session). Calling this again is the refresh — it
 * succeeds only while the Dispatcher session is still valid.
 *
 * The raw `dispatcher_bff_sid` cookie is httpOnly and is never read here; the
 * browser attaches it automatically via `credentials: 'include'`. Mirrors
 * geoSessionApi.ts (native fetch so it is safe outside React).
 */

import { apiRoutes } from '@/shared/api/routes';

export interface ConvAiSessionResponse {
  /** Short-lived opaque Conv-AI session key. Keep in memory only. */
  token: string;
  tokenType: 'Bearer';
  /** ISO-8601 timestamp when the key expires */
  expiresAt: string;
  expiresInSeconds: number;
}

export class ConvAiSessionMintError extends Error {
  constructor(
    message: string,
    /** HTTP status from the BFF, or 0 when the request itself failed */
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ConvAiSessionMintError';
  }
}

export const fetchConvAiSession = async (): Promise<ConvAiSessionResponse> => {
  const response = await fetch(apiRoutes.bff('conv-ai-session'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new ConvAiSessionMintError(
      `[ConvAiSession] Failed to mint key: HTTP ${response.status} ${response.statusText}`,
      response.status,
    );
  }

  const data = (await response.json()) as unknown;
  if (
    typeof data !== 'object' ||
    data === null ||
    typeof (data as Record<string, unknown>).token !== 'string'
  ) {
    throw new ConvAiSessionMintError('[ConvAiSession] Unexpected response shape — missing token field', 0);
  }

  return data as ConvAiSessionResponse;
};
